// Invoice-specific formatting and status logic. No DOM, no state, no API.
import { formatDocumentDate } from "../../../shared/document-format.js";
import { outstandingPenceOf } from "../../../shared/money.js";
import { formatTimeOfDay } from "./format.js";

// What an invoice payload still owes. The rule is shared/money.js
// outstandingPenceOf — total − cash − credit − CIS deduction, floored at zero —
// fed the `…Pence` figures the API sends rather than restating the sum. The CIS deduction counts because the
// contractor pays it to HMRC on your behalf: it settles the invoice without
// ever arriving as cash, the same as a credit note.
//
// Every list, drawer and roll-up that shows a balance reads this. The Invoices
// list once summed total − cash alone, so its Open and Overdue cards counted a
// CIS deduction and credit notes as still owed.
export function invoiceBalance(invoice) {
  const totalPence = Number(invoice?.totalPence) || 0;
  const outstandingPence = outstandingPenceOf({
    totalPence,
    paidPence: invoice?.amountPaidPence,
    creditedPence: invoice?.amountCreditedPence,
    cisDeductionPence: invoice?.cisDeductionAmountPence
  });
  return {
    totalPence,
    outstandingPence,
    total: totalPence / 100,
    outstanding: outstandingPence / 100,
    // Something — cash, credit or CIS — already stands against the total.
    partSettled: totalPence > 0 && outstandingPence < totalPence
  };
}

// Label for a "Due in" option. 0 days means the invoice is payable as soon as
// it lands (due date = issue date) — shown as "On receipt" rather than "0 days".
export function formatDueInOption(days) {
  return Number(days) === 0 ? "On receipt" : `${days} days`;
}

export function formatInvoiceStatus(rawStatus) {
  const normalized = String(rawStatus || "").trim().toLowerCase();
  if (!normalized) return "Draft";
  // Human label — the raw status has an underscore the generic path would keep.
  if (normalized === "partially_paid") return "Part paid";
  if (normalized === "written_off") return "Written off";
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
}

export function getInvoiceStatusTagClass(rawStatus) {
  const normalized = String(rawStatus || "").trim().toLowerCase();
  if (normalized === "paid") return "app-invoice-status-paid";
  if (normalized === "overdue") return "app-invoice-status-overdue";
  // Part paid reads as "in progress, not yet a problem" — blue, distinct from
  // green (paid) and orange (overdue, which a past-due balance still wins).
  if (normalized === "partially_paid") return "app-invoice-status-partial";
  if (normalized === "sent") return "app-invoice-status-sent";
  return "app-invoice-status-draft";
}

export function isInvoicePastDue(rawDueDate) {
  const parsed = new Date(rawDueDate);
  if (Number.isNaN(parsed.getTime())) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
  return due.getTime() < today.getTime();
}

// `isOverduePreferred` is the server's own answer: it knows what the invoice is
// waiting on next (a plan chases its next unpaid instalment, not the invoice's
// own due date) and compares it in the COMPANY's timezone. Pass it whenever the
// payload carries it — `invoice.isOverdue`.
//
// The browser fallback stays for payloads that don't (the recurring-run rows,
// and the demo). It reads a plan invoice as overdue on the invoice-level date,
// which is the bug this parameter exists to fix, so prefer the flag.
//
// null means "the server could not decide", NOT "on time" — hence `!= null`
// rather than a truthiness test.
export function getEffectiveInvoiceStatus(rawStatus, rawDueDate, isOverduePreferred = null) {
  const normalized = String(rawStatus || "").trim().toLowerCase();
  const pastDue = isOverduePreferred != null ? Boolean(isOverduePreferred) : isInvoicePastDue(rawDueDate);
  // Overdue is driven by the balance being unpaid past the due date, so a
  // part-paid invoice past its due date reads "overdue" too (on its balance).
  if ((normalized === "sent" || normalized === "partially_paid") && pastDue) return "overdue";
  return normalized || "draft";
}

// The one place that knows how to ask an invoice payload whether it is late.
// Call sites pass the whole invoice rather than picking fields, so a payload
// that gains the server's answer starts being believed everywhere at once.
export function invoiceIsOverdue(invoice) {
  return getEffectiveInvoiceStatus(invoice?.status, invoice?.dueDate, invoice?.isOverdue ?? null) === "overdue";
}

export function isReadOnlyInvoiceStatus(rawStatus) {
  const normalized = String(rawStatus || "").trim().toLowerCase();
  // partially_paid is read-only: a part-paid invoice opens the status drawer,
  // not the editable compose screen.
  return ["sent", "partially_paid", "paid", "voided", "written_off", "superseded"].includes(normalized);
}

export function formatInvoiceDue(rawDate) {
  if (!rawDate) return "";
  const parsed = new Date(rawDate);
  if (Number.isNaN(parsed.getTime())) return "";
  const isCurrentYear = parsed.getFullYear() === new Date().getFullYear();
  // Short month (Jan/Feb/Mar) in the invoices table — long month elsewhere
  // (the invoice preview / PDF document uses formatInvoicePreviewDate).
  return parsed.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    ...(isCurrentYear ? {} : { year: "numeric" }),
  });
}

// The long-form date on the document face. Always en-GB (see
// shared/document-format.js) so the preview reads exactly as the PDF does.
//
// A real local binding, deliberately. Neither `export … from` nor
// `export { formatDocumentDate as formatInvoicePreviewDate }` would give this
// module an identifier called formatInvoicePreviewDate — and the functions
// below call it by that name.
export const formatInvoicePreviewDate = formatDocumentDate;

// "1 September at 09:00", or "1 September 2027 at 09:00" when it isn't this
// year. Used by the scheduled-send banner, which is about a moment rather than
// a date on the document face — hence the time, and hence dropping a year that
// tells the reader nothing.
//
// Rendered in the COMPANY's timezone, not the reader's. scheduled_send_at is a
// real instant that the server built from the reminder policy's send time in
// that zone, so a laptop set an hour off would otherwise report a send time
// nobody configured — "10:00" for a 9am reminder policy. The zone comes back
// with the invoice as scheduledSendTimezone.
export function formatScheduledSend(rawTimestamp, timeZone = "") {
  // Falsy first: `new Date(null)` is the epoch, not an invalid date, so a
  // missing value would otherwise render as "1 January 1970 at 01:00".
  if (!rawTimestamp) return "";
  const parsed = rawTimestamp instanceof Date ? rawTimestamp : new Date(rawTimestamp);
  if (Number.isNaN(parsed.getTime())) return "";
  // An unknown zone throws rather than falling back, and a banner is never
  // worth a blank screen for.
  let zone = {};
  if (timeZone) {
    try {
      new Intl.DateTimeFormat("en-GB", { timeZone });
      zone = { timeZone };
    } catch {
      zone = {};
    }
  }
  // The year comparison has to happen in the same zone as the date it labels,
  // or a New Year's Eve send is "this year" by one clock and not the other.
  const yearThere = Number(parsed.toLocaleString("en-GB", { ...zone, year: "numeric" }));
  const isCurrentYear = yearThere === new Date().getFullYear();
  const date = parsed.toLocaleDateString("en-GB", {
    ...zone,
    day: "numeric",
    month: "long",
    ...(isCurrentYear ? {} : { year: "numeric" })
  });
  return `${date} at ${formatTimeOfDay(parsed, zone)}`;
}

export function formatDueWithDaysLeft(rawDueDate) {
  const parsed = new Date(rawDueDate);
  if (Number.isNaN(parsed.getTime())) return "";
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
  const diffDays = Math.round((due.getTime() - today.getTime()) / (24 * 60 * 60 * 1000));
  const formatted = formatInvoicePreviewDate(parsed);
  if (diffDays > 1) return `${formatted} (${diffDays} days left)`;
  if (diffDays === 1) return `${formatted} (1 day left)`;
  if (diffDays === 0) return `${formatted} (due today)`;
  if (diffDays === -1) return `${formatted} (1 day overdue)`;
  return `${formatted} (${Math.abs(diffDays)} days overdue)`;
}

export function formatPaidWithTiming(rawPaidDate, rawDueDate) {
  const paid = new Date(rawPaidDate);
  if (Number.isNaN(paid.getTime())) return "";
  const due = new Date(rawDueDate);
  if (Number.isNaN(due.getTime())) return `${formatInvoicePreviewDate(paid)}`;
  const paidDay = new Date(paid.getFullYear(), paid.getMonth(), paid.getDate());
  const dueDay = new Date(due.getFullYear(), due.getMonth(), due.getDate());
  const diffDays = Math.round((paidDay.getTime() - dueDay.getTime()) / (24 * 60 * 60 * 1000));
  if (diffDays <= 0) return `${formatInvoicePreviewDate(paid)} (on time)`;
  if (diffDays === 1) return `${formatInvoicePreviewDate(paid)} (1 day late)`;
  return `${formatInvoicePreviewDate(paid)} (${diffDays} days late)`;
}
